import React, { useState, useEffect, useCallback, useRef, useMemo } from "react"
import { InfoCircleOutlined } from "@ant-design/icons"
import { Alert, Form, Row, Col, Select, DatePicker, Input, Switch, Space, Spin, Flex, Typography, Tooltip } from "antd"
import dayjs from "dayjs"
import { debounce } from "lodash"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { useSelector, useDispatch } from "react-redux"
import { useWatch } from "antd/es/form/Form"
import {
  fetchExperienceById,
  fetchExperienceBookingDetails,
  resetExperienceBookingData,
  resetTripAvailableSlots,
  resetExperienceById
} from "store/experiences"
import { communicationNotificationStatusSelector } from "store/communication"
import { BOOKING_SOURCE } from "pagesV2/pro/Bookings/constants"
import { getAssetAllocation, cleanAssetAllocation } from "store/bookings"
import { setSelectedPaymentMethodId } from "store/user"
import { isFeatureEnabled } from "config/features"
import TimeDurationSelector from "./TimeDurationSelector"
import GuestInputGroup from "./GuestInputGroup"
import GuestCountSection from "./GuestCountSection"
import {
  calculateBookingTotal,
  buildBreakdownFromPaymentSummary,
  computeGuestSubtotalFromPaymentSummary
} from "../helpers/calculateBookingTotal"
import { getSeasonalSpanWarning } from "../helpers/getSeasonalSpanWarning"
import { DateSelector, DurationSelector } from "ui/BookingCalendar"
import bookingCalendarStyles from "ui/BookingCalendar/BookingCalendar.module.css"
import { getFixedDurationOptions, isDynamicMultiDay, isFixedMultiDay } from "../helpers/multiDayPricing"
import { hasNoUserBookingChanges, resolveEditPaymentType, getPriceAdjustmentTotal } from "../bookingUtils"
import { resolveEffectiveDiscountPromos } from "utils/discountUtils"
import { getCardFeeFraction, getPriorCardCharge, getSavedBookingTotals, hasCollectedPayment } from "utils/bookingCalculations"

const { Text } = Typography

const { Option } = Select
const { TextArea } = Input

const BOOKING_DATE_FORMAT = "MM/DD/YYYY"

// "11/11/2027" for a single day, "11/11/2027 \u2013 11/14/2027" for a run of them.
const formatDateSpan = (dates) => {
  const first = dates[0].format(BOOKING_DATE_FORMAT)
  const last = dates[dates.length - 1].format(BOOKING_DATE_FORMAT)
  return first === last ? first : `${first} \u2013 ${last}`
}

const SpinningPanel = ({ panel, loading }) => <Spin spinning={loading}>{panel}</Spin>

SpinningPanel.propTypes = {
  panel: PropTypes.node,
  loading: PropTypes.bool
}

const AssetAllocationStatus = ({ assetAllocation }) => (
  <>
    {assetAllocation.loading && (
      <Flex align="center" gap={8} className="py-xs">
        <Spin size="small" />
        <Text>Calculating asset allocation...</Text>
      </Flex>
    )}
    {assetAllocation.count !== null && !assetAllocation.loading && !assetAllocation.error && (
      <Flex className="py-xs">
        <Text>
          {assetAllocation.count} {assetAllocation.count === 1 ? "asset" : "assets"} will be allocated for this booking based on selected
          guests and asset capacity
        </Text>
      </Flex>
    )}
    {assetAllocation.error && !assetAllocation.loading && (
      <Flex className="py-xs">
        <Text type="danger">{assetAllocation.errorMessage || "Insufficient asset capacity for the selected guests and time slot."}</Text>
      </Flex>
    )}
  </>
)

AssetAllocationStatus.propTypes = {
  assetAllocation: PropTypes.shape({
    loading: PropTypes.bool,
    count: PropTypes.number,
    error: PropTypes.bool,
    errorMessage: PropTypes.string
  }).isRequired
}

const NotifyClientField = ({ t, form, clientNotify, isNotifyLocked, notifyError, setNotifyError }) => {
  const handleNotifyChange = (val) => {
    if (isNotifyLocked && !val) {
      setNotifyError(t("booking.error_messages.marketplace_notify_locked"))
      return
    }
    setNotifyError("")
    form.setFieldsValue({ client_notify: val, send_guest_email: val })
  }

  return (
    <Form.Item name="client_notify" valuePropName="checked">
      <Space align="center" direction="vertical" style={{ alignItems: "flex-start" }}>
        <Space align="center">
          <Switch value={clientNotify} disabled={isNotifyLocked} onChange={handleNotifyChange} />
          <span>{t("booking.labels.notify_client")}</span>
          {isNotifyLocked && (
            <Tooltip title={t("booking.error_messages.marketplace_notify_locked")}>
              <InfoCircleOutlined style={{ color: "#faad14", cursor: "default" }} />
            </Tooltip>
          )}
        </Space>
        {notifyError && <span style={{ color: "#ff4d4f", fontSize: 12 }}>{notifyError}</span>}
      </Space>
    </Form.Item>
  )
}

NotifyClientField.propTypes = {
  t: PropTypes.func.isRequired,
  form: PropTypes.object.isRequired,
  clientNotify: PropTypes.bool,
  isNotifyLocked: PropTypes.bool,
  notifyError: PropTypes.string,
  setNotifyError: PropTypes.func.isRequired
}

const DurationField = ({ t, durationLoading, updatedDurationSlots, selectedValue, onChange, disabled }) => (
  <Col xs={24} sm={24}>
    <Form.Item label={t("booking.labels.duration")} name="duration" rules={[{ required: true, message: t("booking.error_messages.duration_required") }]}>
      {durationLoading ? (
        <div style={{ textAlign: "center", padding: "12px 0" }}>
          <Spin size="small" />
        </div>
      ) : (
        <TimeDurationSelector options={updatedDurationSlots || []} selectedValue={selectedValue} disabled={disabled} onChange={onChange} />
      )}
    </Form.Item>
  </Col>
)

DurationField.propTypes = {
  t: PropTypes.func.isRequired,
  durationLoading: PropTypes.bool,
  updatedDurationSlots: PropTypes.array,
  selectedValue: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool
}

const TimeSlotField = ({ t, timeLoading, updatedTimeSlots, selectedValue, onChange, disabled }) => (
  <Col xs={24} sm={24}>
    <Form.Item label={t("booking.labels.booked_time")} name="time" rules={[{ required: true, message: t("booking.error_messages.time_required") }]}>
      {timeLoading ? (
        <div style={{ textAlign: "center", padding: "12px 0" }}>
          <Spin size="small" />
        </div>
      ) : (
        <TimeDurationSelector options={updatedTimeSlots} selectedValue={selectedValue} disabled={disabled} onChange={onChange} />
      )}
    </Form.Item>
  </Col>
)

TimeSlotField.propTypes = {
  t: PropTypes.func.isRequired,
  timeLoading: PropTypes.bool,
  updatedTimeSlots: PropTypes.array,
  selectedValue: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool
}


const GuestSection = ({
  isBookingFixed,
  isPrivateExperience,
  bookingSelections,
  form,
  handleGuestsChange,
  editingBookingUuid,
  assetClientParams,
  showPrivateGuestSection,
  selectedTripPrice,
  handlePrivateGuestChange,
  experienceDetail,
  assetAllocation
}) => (
  <>
    {!isPrivateExperience && bookingSelections?.timeSlot && (
      <Col xs={24} sm={24}>
        <GuestInputGroup
          form={form}
          disabled={isBookingFixed}
          setCounts={handleGuestsChange}
          bookingSelections={bookingSelections}
          editingBookingUuid={editingBookingUuid}
          clientParams={assetClientParams}
        />
      </Col>
    )}
    {showPrivateGuestSection && (
      <Col xs={24} sm={24}>
        <GuestCountSection
          form={form}
          isDisabled={isBookingFixed}
          tripPrice={selectedTripPrice}
          onChange={handlePrivateGuestChange}
          minGuests={experienceDetail?.min_guests}
          maxGuests={experienceDetail?.max_guests}
          bookingSelections={bookingSelections}
        />
        {isFeatureEnabled("NEW_RESOURCES") && experienceDetail?.auto_assign_equipment && (
          <AssetAllocationStatus assetAllocation={assetAllocation} />
        )}
      </Col>
    )}
  </>
)

const AccessibilityNotesField = ({ t, accessibilityRequired }) =>
  accessibilityRequired ? (
    <Form.Item name="accessibility_notes" rules={[{ required: accessibilityRequired, message: t("booking.error_messages.accessibility_notes_required") }]}>
      <TextArea placeholder={t("booking.labels.accessibility_notes")} rows={3} />
    </Form.Item>
  ) : null

AccessibilityNotesField.propTypes = {
  t: PropTypes.func.isRequired,
  accessibilityRequired: PropTypes.bool
}

GuestSection.propTypes = {
  isBookingFixed: PropTypes.bool,
  isPrivateExperience: PropTypes.bool,
  bookingSelections: PropTypes.object,
  form: PropTypes.object.isRequired,
  handleGuestsChange: PropTypes.func.isRequired,
  editingBookingUuid: PropTypes.string,
  assetClientParams: PropTypes.object,
  showPrivateGuestSection: PropTypes.bool,
  selectedTripPrice: PropTypes.object,
  handlePrivateGuestChange: PropTypes.func.isRequired,
  experienceDetail: PropTypes.object,
  assetAllocation: PropTypes.object
}

const WEEKDAY_NAME_TO_INDEX = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6
}

// Pulled out of the component so its branching doesn't count toward ExperienceDetail's own
// complexity — pure function of the date being checked and the current experience's constraints.
// Pure derivation, pulled out of the component for the same reason as isDateDisabledForExperience.
const shouldAddSavedTimeSlot = ({ isEdit, existingTimeSlotValue, isExistingSlotPresent, selectedTripLength, booking, bookingSelections }) =>
  !!(
    isEdit &&
    existingTimeSlotValue &&
    !isExistingSlotPresent &&
    selectedTripLength?.value === booking?.selected_trip_duration &&
    bookingSelections?.date === booking?.booked_date
  )

// Pulled out of the component for the same reason as isDateDisabledForExperience above (see that
// comment for the full rationale on why this freshness check exists at all).
const resolveExperienceFreshness = ({ selectedExperience, isEdit, experienceDetail, booking }) => {
  const selectedExperienceUuid = selectedExperience?.value || (isEdit && !experienceDetail?.id ? booking?.trip?.uuid : null)
  const selectedExperienceKeyForFreshness =
    selectedExperience?.key ?? (isEdit && selectedExperienceUuid === booking?.trip?.uuid ? booking?.trip?.id : null)
  const isExperienceDetailCurrent =
    selectedExperienceKeyForFreshness == null || String(experienceDetail?.id) === String(selectedExperienceKeyForFreshness)
  return { selectedExperienceUuid, isExperienceDetailCurrent }
}

const filterExperiencesByIds = (experiences, filterExperienceIds) =>
  filterExperienceIds && filterExperienceIds.length > 0 ? experiences.filter((exp) => filterExperienceIds.includes(exp.id)) : experiences

const isPrivateGuestSectionVisible = (isPrivateExperience, privateDurationType, bookingSelections) =>
  !!(
    isPrivateExperience &&
    ((privateDurationType === "single_day" && bookingSelections?.timeSlot) || (privateDurationType === "multi_day" && bookingSelections?.date))
  )

const isDateDisabledForExperience = (current, experienceDetail, scheduledDeletionDate) => {
  const date = dayjs(current)
  if (date.isBefore(dayjs(), "day")) return false

  const blockedWeekdays = experienceDetail?.unavailable_weekdays?.map((day) => WEEKDAY_NAME_TO_INDEX[day.toLowerCase()]) || []
  const isSoldOut = experienceDetail?.sold_out_dates?.some((dateStr) => date.isSame(dayjs(dateStr), "day"))
  const isUnavailable = experienceDetail?.unavailable_dates?.some((dateStr) => date.isSame(dayjs(dateStr), "day"))
  const hasDateRanges = Array.isArray(experienceDetail?.available_date_ranges) && experienceDetail.available_date_ranges.length > 0
  const isInAvailableRange = !hasDateRanges
    ? true
    : experienceDetail?.available_date_ranges.some((range) => {
        const start = dayjs(range.start).startOf("day")
        const end = dayjs(range.end).endOf("day")
        return date.isBetween(start, end, null, "[]")
      })

  return (
    date.isAfter(scheduledDeletionDate) ||
    blockedWeekdays.includes(date.day()) ||
    isUnavailable ||
    isSoldOut ||
    (hasDateRanges && !isInAvailableRange)
  )
}

const ExperienceDetail = ({
  form,
  bookingId,
  booking,
  isEdit,
  bookAgain,
  mode,
  filterExperienceIds,
  cardFeeInfo,
  appliedPromos = [],
  isExperienceChanged = false,
  isOriginalDiscountRemoved = false,
  isPromoChanged = false,
  // DS-4473. Recovery moves an expired request to a new slot — the experience and guest count are
  // what the guest already asked for and paid for, so they stay fixed while the schedule changes.
  isRecovery = false,
  // DS-4473. Post-trip charges freeze the opposite half: the guest count is what it was on the day,
  // while the experience and schedule can be corrected after the fact (AC-20, AC-21).
  isPostTrip = false,
  bookingSelections,
  updateBookingSelections,
  dateError = false,
  setDateError,
  bookingSource
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  // DS-4473. The two restricted modes freeze different things. Recovery re-books the *same* trip on
  // a new slot, so its experience stays fixed. A post-trip adjustment may now move the experience
  // and the slot too — what stays frozen there is the guest count, because who actually went on the
  // trip is a matter of record rather than something to re-decide afterwards.
  const isExperienceFixed = isRecovery
  const isGuestCountFixed = isRecovery || isPostTrip

  // The booking being edited must be excluded from the asset-capacity check, otherwise the backend
  // counts its own equipment as taken and answers 422. "Book again" reuses the edit screen but
  // creates a new booking, so it must not exclude anything.
  const editingBookingUuid = isEdit && !bookAgain ? bookingId : null

  const [durationLoading, setDurationLoading] = useState(false)
  const [timeLoading, setTimeLoading] = useState(false)
  const [datePickerOpen, setDatePickerOpen] = useState(false)
  const [notifyError, setNotifyError] = useState("")
  const isNotifyLocked = bookingSource === BOOKING_SOURCE.marketplace || bookingSource === BOOKING_SOURCE.website
  const lastSlotsFetchRef = useRef(null)

  const {
    list: experiences,
    loading: experiencesListLoading,
    experienceById: { experienceDetail, loading: experienceLoading },
    experienceBookingData
  } = useSelector((state) => state.experiences)
  const assetAllocation = useSelector((state) => state.bookings.assetAllocation)
  // Set when a client is picked in the existing-client dropdown; the backend uses it to tailor the
  // available slots to that client. Same id already sent as `client_id` on the booking payload.
  const selectedClientId = useSelector((state) => state.bookings.clientId)

  const isPrivateExperience = !!experienceDetail?.duration_type
  const privateDurationType = experienceDetail?.duration_type
  // Duration/time-slot selection doesn't apply to multi_day. Only render those fields once the
  // experience detail has actually loaded — otherwise (e.g. while editing a multi_day booking whose
  // API still returns a duration/time) they flash with stale "0 hrs"/slot values before the detail
  // resolves and the multi_day type is known.
  const durationTimeApplicable = !experienceLoading && !!experienceDetail && privateDurationType !== "multi_day"

  const displayedExperiences = filterExperiencesByIds(experiences, filterExperienceIds)

  const { scheduled_deletion_date } = useSelector((state) => state.user?.guide) || {}
  const notificationStatus = useSelector(communicationNotificationStatusSelector)
  // "Collect payment now? No" charges no card on this save, so no new processing fee is
  // quoted. Undefined means the question has not been reached yet, which behaves as before.
  const isCollectingNow = useWatch("collectPayment", form) !== "no"

  const accessibilityRequired = useWatch("accessibility_required", form)
  const clientNotify = useWatch("client_notify", form)
  const selectedExperience = useWatch("experience", form)
  const paymentType = useWatch("paymentType", form)
  const depositOnly = useWatch("depositOnly", form)
  const depositPercent = useWatch("depositPercent", form)
  const customDepositAmount = useWatch("customDepositAmount", form)
  const depositFraction = getCardFeeFraction({
    depositOnly,
    depositPercent,
    customDepositAmount
  })
  const date = useWatch("date", form)
  const selectedExperienceId = selectedExperience?.value

  // Whether `experienceDetail` (fetched async off the selected experience's uuid) has actually
  // caught up to the CURRENTLY selected experience, vs. still describing the one just switched away
  // from. The dropdown's `experience` field, and `selectedExperienceId` above, update synchronously
  // on selection, but `experienceDetail` only updates once its fetch resolves — and the date picker
  // is only reliably disabled (via `experienceLoading`) after that fetch has been DISPATCHED, not from
  // the instant of selection. So there's a real window, right after switching experiences, where the
  // date picker is still interactive while `experienceDetail`/`privateDurationType` still describe the
  // OLD experience. Anything deriving trip-price data from `experienceDetail` (handleDateChange, the
  // multi_day tripLength seed effect below) must gate on this, or it can seed `tripLength` with the
  // OLD experience's trip_price — which then survives untouched once Step 2 is reached, since
  // ExperienceDetail unmounts there and nothing else re-validates it before submit.
  const { selectedExperienceUuid, isExperienceDetailCurrent } = resolveExperienceFreshness({
    selectedExperience,
    isEdit,
    experienceDetail,
    booking
  })

  // Client identity for the asset-capacity check, sent on both create and edit. `clientId` is set by
  // the existing-client dropdown on create and by populateFormWithBooking on edit; a brand-new client
  // has no id yet, so fall back to the typed email.
  const clientEmail = useWatch("email", form)
  const assetClientParams = useMemo(() => {
    if (selectedClientId) return { client_id: selectedClientId }
    if (clientEmail) return { client_email: clientEmail }
    return null
  }, [selectedClientId, clientEmail])

  // Negotiated subtotal (set at creation, carried into edit): override the computed guest subtotal
  // so the adjusted price holds through guest-count changes. A no-op when no adjustment exists. In
  // edit mode, fall back to the booking's stored adjusted subtotal so the override holds even before
  // the watched form field settles.
  const adjustedPrice = useWatch("adjustedPrice", form)
  let adjustedBase = null
  if (adjustedPrice != null) {
    adjustedBase = adjustedPrice
  } else if (isEdit && booking?.payment_summary?.subtotal_adjusted) {
    adjustedBase = Number(booking?.payment_summary?.adjusted_subtotal)
  }
  const overrideGuestSubtotal = adjustedBase != null && adjustedBase > 0 ? adjustedBase : undefined
  // Carry the post-creation price adjustment (details-page "Adjust Booking Price") into edit recalcs
  // so guest-count/duration changes keep the manual price change. Edit mode only (isEdit is unset in
  // the new-booking / book-again flows, which have no adjustment to carry).
  const priceAdjustmentTotal = isEdit ? getPriceAdjustmentTotal(booking) : 0

  const effectivePaymentType = resolveEditPaymentType(paymentType, booking, isEdit)

  // ── Regular-experience slot/duration data ──────────────────────────────────
  const slotData = experienceBookingData?.bookingData?.data
  const timeSlots = (Array.isArray(slotData) ? slotData : []).map((item) => {
    const slot = typeof item === "object" ? item.slot : item
    return { key: { slot, available: item?.available !== false }, value: slot }
  })

  const baseDurations = useMemo(() => {
    const durations = experienceBookingData?.bookingData?.trip_durations
    return Array.isArray(durations) ? durations : []
  }, [experienceBookingData?.bookingData?.trip_durations])
  const selectedTripLength = bookingSelections?.tripLength
  const isTripLengthIncluded = selectedTripLength ? baseDurations.some((slot) => slot.key === selectedTripLength.key) : true

  // Resolve the trip price for the currently selected duration so guest-type flags (adults_allowed,
  // children_allowed, etc.) always reflect the chosen duration, not just the first price.
  const selectedTripPrice = useMemo(() => {
    const fromTripLength = bookingSelections?.tripLength?.price
    if (fromTripLength) return fromTripLength
    const key = bookingSelections?.tripLength?.key
    if (key && experienceDetail?.trip_prices?.length) {
      return experienceDetail.trip_prices.find((p) => p.id === key) ?? experienceDetail.trip_prices[0]
    }
    return experienceDetail?.trip_prices?.[0] ?? null
  }, [bookingSelections?.tripLength, experienceDetail?.trip_prices])

  // The calendar's own day/seasonal + progressive price preview must read the SAME trip_price
  // as selectedTripPrice above — passing the whole experienceDetail lets useDailyDiscountCalendar's
  // internal trip_prices lookup silently default to trip_prices[0], so switching duration cards
  // on a fixed multi-day trip updated the "From $X" summary but left every calendar day still
  // showing the first duration's rate.
  const calendarTrip = useMemo(
    () => (selectedTripPrice ? { ...experienceDetail, trip_prices: [selectedTripPrice] } : experienceDetail),
    [experienceDetail, selectedTripPrice]
  )
  const updatedDurationSlots = useMemo(
    () => (isEdit && !isTripLengthIncluded ? [...baseDurations, selectedTripLength] : baseDurations),
    [isEdit, isTripLengthIncluded, baseDurations, selectedTripLength]
  )

  // Pre-select a duration as soon as the list loads, rather than waiting for a date — a single-day
  // experience's duration (and the price it drives on the calendar) shouldn't be gated on the guide
  // picking a date first. Only auto-picks when nothing valid is already chosen: the saved booking's
  // own duration (once present in updatedDurationSlots) is kept rather than swapped for the
  // experience's first row, which would drop the unit prices the booking was charged
  // (withSoldUnitPrices) and re-price an add-on-only edit.
  useEffect(() => {
    if (!updatedDurationSlots.length) return
    const hasValidSelection =
      selectedTripLength?.key != null && updatedDurationSlots.some((slot) => slot.key === selectedTripLength.key)
    if (hasValidSelection) return
    const firstSlot = updatedDurationSlots[0]
    form.setFieldsValue({ duration: firstSlot.value })
    updateBookingSelections({ tripLength: firstSlot })
  }, [updatedDurationSlots, form, updateBookingSelections, selectedTripLength?.key])

  useEffect(() => {
    if (isEdit || form.getFieldValue("client_notify") !== undefined) return
    const defaultNotify = notificationStatus.booking_confirmation_email_allowed
    form.setFieldsValue({ client_notify: defaultNotify, send_guest_email: defaultNotify })
  }, [notificationStatus.booking_confirmation_email_allowed, isEdit, form])

  // Durations and slots are client-dependent, so switching client retires whatever was picked for the
  // previous one. Trigger on the client actually changing rather than on the selection disappearing
  // from the refreshed list: trip_data and available_slots share one merged `bookingData` bucket, so
  // mid-flight a membership test reads the wrong list and would clear a valid selection.
  // Skipped on the first run (ref seeded with the current client) so edit keeps its saved duration
  // when populateFormWithBooking sets the client after mount.
  const lastClientIdRef = useRef(selectedClientId)
  useEffect(() => {
    if (lastClientIdRef.current === selectedClientId) return
    lastClientIdRef.current = selectedClientId
    if (isEdit || privateDurationType === "multi_day") return
    updateBookingSelections({ tripLength: null, timeSlot: null })
    form.setFieldsValue({ duration: null, time: null })
  }, [selectedClientId, isEdit, privateDurationType, updateBookingSelections, form])

  const existingTimeSlotValue = booking?.start_time && booking?.end_time ? `${booking.start_time} - ${booking.end_time}` : ""
  const isExistingSlotPresent = timeSlots.some((slot) => slot.value === existingTimeSlotValue)
  const shouldAddTimeSlot = shouldAddSavedTimeSlot({
    isEdit,
    existingTimeSlotValue,
    isExistingSlotPresent,
    selectedTripLength,
    booking,
    bookingSelections
  })

  // Recovery offers exactly the slots an ordinary edit does — whatever the backend returns for the
  // chosen date, with `available` and the booking cutoff its call. The one difference: an ordinary
  // edit re-adds the booking's own saved slot so it doesn't vanish from the list, which recovery
  // must not do, because that slot is the one that expired.
  const updatedTimeSlots = useMemo(
    () => (shouldAddTimeSlot && !isRecovery ? [...timeSlots, { key: existingTimeSlotValue, value: existingTimeSlotValue }] : timeSlots),
    [shouldAddTimeSlot, timeSlots, existingTimeSlotValue, isRecovery]
  )

  useEffect(() => {
    if (!timeLoading && updatedTimeSlots.length === 1 && !bookingSelections?.timeSlot) {
      const onlySlot = updatedTimeSlots[0]
      form.setFieldsValue({ time: onlySlot.value })
      updateBookingSelections({ timeSlot: onlySlot.key?.slot || onlySlot.key })
    }
  }, [updatedTimeSlots, timeLoading, bookingSelections?.timeSlot, form, updateBookingSelections])

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleExperienceChange = (experience) => {
    // The old experience's add-ons don't carry over to a different experience — clear them so the
    // user starts from the new experience's add-ons. (The original discount is dropped separately
    // via isExperienceChanged in the parent.)
    const clearedSchedule = { date: null, timeSlot: null, tripLength: null, addOns: {}, addOnsTotal: 0 }
    form.setFieldsValue({
      experience,
      date: "",
      // DS-4473. On a completed booking the guest counts record who actually went and aren't editable,
      // so they survive the swap and the new experience is priced against them.
      ...(isGuestCountFixed ? {} : { main: { adults: 0, children: 0, infants: 0, passengers: 0 } }),
      // Payment-related fields are tied to the previous experience's fee configuration (payment
      // type drives whether step 1's price-calc effect below includes tax/card-fee, and the
      // deposit/payment-option fields are meaningless without it). Left set, they'd make step 1
      // silently compute a fee-inclusive total for the new experience before the guide ever
      // reaches the Payment step — using the wrong card-fee rate, since this component's
      // calculateBookingTotal calls have no cardFeeInfo. Reset to the same values BookingForm's
      // `initialValues` (constants.js) uses for a fresh booking. This also covers depositPercent/
      // customDepositAmount, which otherwise carry the OLD experience's deposit rate and win every
      // `useWatch("depositPercent", form) || experienceDetail?.deposit_percentage` fallback in
      // EditBookingForm/PriceBreakdown/PaymentMethod — keeping "Deposit Due Now" (and the actual
      // amount charged) on the OLD percentage after switching experiences.
      paymentType: undefined,
      depositOnly: false,
      depositPercent: null,
      customDepositAmount: null,
      paymentOption: "",
      collectPayment: "yes",
      // "Adjust subtotal" (PriceBreakdown.jsx) is a manually-typed override subtotal for the
      // previous experience — calculateBookingTotal uses it unconditionally when present, so left
      // set it would silently become the new experience's subtotal.
      adjustedPrice: null,
      adjustmentReason: null
    })
    updateBookingSelections({
      ...clearedSchedule,
      // Persist the selected experience uuid here (not just the form field) so the parent can detect
      // an experience change on step 2 as well — where this component is unmounted and a form watch
      // would read undefined.
      experienceUuid: experience?.value ?? null,
      // updateBookingSelections shallow-merges, so a dynamic multi-day endDate picked on the
      // previous experience would otherwise survive the switch — stale-pairing with the new
      // experience's (unrelated) start date in the payload, the step-2 summary, and the
      // Continue validation gate below.
      endDate: null,
      ...(isGuestCountFixed ? {} : { counts: null }),
      amount: 0,
      baseAmount: 0,
      breakdown: [],
      preCardTotal: 0,
      cardProcessingFee: 0,
      // The old experience's add-ons don't carry over to a different experience — clear them so the
      // user starts from the new experience's add-ons. (The original discount is dropped separately
      // via isExperienceChanged in the parent.)
      addOns: {},
      addOnsTotal: 0
    })
    setDateError?.(false)
    dispatch(resetExperienceBookingData())
    dispatch(resetTripAvailableSlots())
    dispatch(cleanAssetAllocation())
    // Clears the previous experience's cached detail (and flips loading back to false) immediately,
    // synchronously with the resets above — otherwise the multi-day tripLength-seeding effect further
    // down can read the OLD experience's trip_prices for one render before fetchExperienceById (fired
    // by the effect that watches the selected experience) resolves, permanently snapshotting the
    // wrong rate into bookingSelections.tripLength.price.
    dispatch(resetExperienceById())
    // A card selected for the previous experience/client pairing shouldn't silently carry over and
    // drive PaymentMethod.jsx's fee recalculation for the new experience.
    dispatch(setSelectedPaymentMethodId(null))
    setDatePickerOpen(true)
  }

  const disableDates = useCallback(
    (current) => isDateDisabledForExperience(current, experienceDetail, scheduled_deletion_date),
    [experienceDetail, scheduled_deletion_date]
  )

  // Multi-day booking, mirroring the guest flow: the guide picks a duration, then a start date, and
  // the block the duration covers is painted on the calendar.
  const isMultiDayExperience = privateDurationType === "multi_day"
  // True while experienceDetail says this is a multi_day trip but the REAL per-day availability
  // (available_dates/booked_dates/max_span_by_date) hasn't landed yet. That data comes from a second,
  // later fetch (fetchExperienceBookingDetails "available_slots") that only starts once tripLength is
  // seeded from experienceDetail — itself only after fetchExperienceById resolves — so it is not
  // necessarily done by the time `experienceLoading` (the gate below was originally scoped to) flips
  // false. Without this, the calendar goes interactive for a window where DateSelector has no real
  // availableDates/bookedDates and silently falls back to weak trip-level rules that can't see e.g. a
  // date range already consumed by another booking.
  //
  // Primarily checked as "has the current selection's data actually arrived" rather than relying on
  // the shared `experienceBookingData.loading` flag alone, because relying on it alone would miss the
  // narrow window where tripLength has just been seeded but the effect that dispatches the
  // availability fetch hasn't run yet (loading still false, data still stale/absent).
  //
  // `experienceBookingData?.loading` is still added as an ADDITIONAL disjunct below to cover a
  // separate gap: switching duration on a fixed multi-day trip via DurationSelector changes
  // tripLength.price.id to the new duration's id immediately, but `receivedExperienceBookingData`
  // MERGES each fetch's payload into `bookingData` rather than replacing it — so `available_dates`
  // still holds the PREVIOUS duration's data while the new duration's fetch is in flight, and neither
  // of the two checks above would catch that. Safe to add here: within this `isMultiDayExperience`
  // branch, the single-day trip_data/time-slot effects that also touch this loading flag have already
  // early-returned (they gate on `privateDurationType !== "multi_day"`), so it can only reflect the
  // multi-day "available_slots" fetch while this branch is rendering.
  //
  // Releasing on `error` avoids spinning forever if the fetch fails.
  const isMultiDayAvailabilityPending =
    isMultiDayExperience &&
    !experienceBookingData?.error &&
    (!bookingSelections?.tripLength?.price?.id ||
      !experienceBookingData?.bookingData?.data?.available_dates ||
      experienceBookingData?.loading)
  const isFixedDays = isFixedMultiDay(experienceDetail)
  const isDynamicDays = isDynamicMultiDay(experienceDetail)
  const fixedDurationOptions = useMemo(() => getFixedDurationOptions(experienceDetail), [experienceDetail])
  const showDurationCards = isFixedDays && fixedDurationOptions.length > 1
  const selectedTripDays = isFixedDays ? Number(bookingSelections?.tripLength?.price?.duration_days ?? bookingSelections?.tripLength?.price?.trip_duration) || 0 : 0
  const minDurationDays = Number(experienceDetail?.min_duration_days) || 0
  const maxDurationDays = Number(experienceDetail?.max_duration_days) || 0

  const handleDateChange = useCallback(
    (value) => {
      setDatePickerOpen(false)
      const formattedDate = dayjs(value).format("YYYY-MM-DD")
      // Guard against a stale `experienceDetail` that still describes the experience just switched
      // AWAY from: the date picker isn't reliably disabled (via `experienceLoading`) until the fetch
      // for the newly-selected experience has actually been dispatched, so there's a real window,
      // right after switching, where a date can be picked while `experienceDetail`/`privateDurationType`
      // still belong to the OLD experience. Trusting them here would seed `tripLength` with the OLD
      // experience's trip_price — which then survives untouched into Step 2, since ExperienceDetail
      // unmounts there and nothing re-validates `tripLength` before "Update Trip" submits it.
      const isMultiDay = isExperienceDetailCurrent && privateDurationType === "multi_day"
      const tripPrice = experienceDetail?.trip_prices?.[0]
      // On multi-day the duration is chosen before the date, so keep whatever is already selected
      // instead of snapping back to the first booking option.
      const keepTripLength = isMultiDay && bookingSelections?.tripLength?.price
      updateBookingSelections({
        date: formattedDate,
        timeSlot: null,
        ...(keepTripLength
          ? {}
          : { tripLength: isMultiDay && tripPrice ? { key: tripPrice.id, value: "", price: tripPrice } : null })
      })
      form.setFieldsValue({ date: value, ...(isMultiDay ? {} : { duration: null, time: null }) })
      setDateError?.(false)
    },
    [
      updateBookingSelections,
      form,
      privateDurationType,
      experienceDetail?.trip_prices,
      bookingSelections?.tripLength?.price,
      setDateError,
      isExperienceDetailCurrent
    ]
  )

  // Dynamic multi-day: the start lives in `date`, the guide-chosen end in `endDate`. A half-picked
  // range clears the end so the form can't advance on an open range.
  const handleRangeChange = useCallback(
    (range) => {
      updateBookingSelections({
        date: range?.from ? dayjs(range.from).format("YYYY-MM-DD") : null,
        endDate: range?.to ? dayjs(range.to).format("YYYY-MM-DD") : null,
        timeSlot: null
      })
      form.setFieldsValue({ date: range?.from ? dayjs(range.from) : null })
      setDateError?.(false)
    },
    [updateBookingSelections, form, setDateError]
  )

  const handleDurationSelect = useCallback(
    (option) => {
      updateBookingSelections({ tripLength: { key: option.price.id, value: "", price: option.price } })
    },
    [updateBookingSelections]
  )

  const handleTripDurationChange = useCallback(
    (tripLength) => {
      const isTripDurationUnchanged = tripLength?.value === booking?.selected_trip_duration
      setTimeLoading(true)
      const matchingPrice = experienceDetail?.trip_prices?.find((p) => p.id === tripLength.key) ?? experienceDetail?.trip_prices?.[0]
      const enrichedTripLength = matchingPrice ? { ...tripLength, price: matchingPrice } : tripLength

      const isSubtotalAdjusted = !!booking?.payment_summary?.subtotal_adjusted
      const counts = bookingSelections?.counts
      const guideFeePercent = experienceDetail?.guide_booking_fee_percentage || booking?.booking_fee_percentage
      const hasGuestCounts =
        counts && (Object.values(counts.main || {}).some((v) => v > 0) || Object.values(counts.extra || {}).some((v) => v > 0))

      const updatedSelections = {
        tripLength: enrichedTripLength,
        timeSlot: isTripDurationUnchanged ? existingTimeSlotValue : null
      }

      const reprice = () => {
        const tempSelections = { ...bookingSelections, tripLength: enrichedTripLength }
        const { total, guestSubtotal, calculatedGuestSubtotal, breakdown, preCardTotal, cardProcessingFee } = calculateBookingTotal(
          tempSelections,
          counts,
          guideFeePercent,
          {
            paymentType: effectivePaymentType,
            isEdit,
            originalTaxAndFee: booking?.payment_summary?.tax_and_fee,
            customFees: experienceDetail?.assigned_custom_fees,
            platformFeeSetting: experienceDetail?.platform_fee_setting || booking?.platform_fee_setting,
            addOnsCatalog: experienceDetail?.add_ons,
            cardFeeInfo,
            originalCardProcessingFee: booking?.payment_summary?.card_processing_fee,
          priorCardCharge: getPriorCardCharge(booking),
          isCollectingNow,
          hasCollectedPayment: hasCollectedPayment(booking),
            discountPromos: resolveEffectiveDiscountPromos(appliedPromos, booking, isEdit && !isExperienceChanged && !isOriginalDiscountRemoved),
            dayAdjustments: experienceDetail?.day_adjustments,
            seasonalAdjustments: experienceDetail?.seasonal_adjustments,
            seasonRanges: experienceDetail?.available_date_ranges,
            trip: experienceDetail,
            depositFraction,
            overrideGuestSubtotal,
            priceAdjustmentTotal
          }
        ) || { total: 0, guestSubtotal: 0, calculatedGuestSubtotal: 0, breakdown: [], preCardTotal: 0, cardProcessingFee: 0 }
        updatedSelections.amount = total
        updatedSelections.baseAmount = guestSubtotal
        updatedSelections.originalBaseAmount = calculatedGuestSubtotal
        updatedSelections.breakdown = breakdown
        updatedSelections.preCardTotal = preCardTotal
        updatedSelections.cardProcessingFee = cardProcessingFee
      }

      if (isGuestCountFixed) {
        // DS-4473. Locked guest counts — who actually went on a completed trip, or who a recovered
        // request was already paid for — aren't editable, so a duration change reprices against them
        // rather than clearing them the way the final branch below does. Recovery counts too: moving
        // an expired request to a slot with a different duration must not drop its guests.
        if (hasGuestCounts) reprice()
      } else if (isSubtotalAdjusted) {
        // Adjusted booking: the base is pinned to the negotiated price, so a duration change (or the
        // duration re-selection after a date change) must not move the total or wipe the guest
        // counts — keep everything as-is so the "price not changed" view is shown. Recompute only when
        // add-ons already differ, and even then the base stays the adjusted price (via override).
        if (hasGuestCounts && !hasNoUserBookingChanges({ ...bookingSelections, tripLength: enrichedTripLength }, booking, isExperienceChanged, isPromoChanged)) {
          reprice()
        }
      } else if (isTripDurationUnchanged) {
        // Editing an existing booking with its original duration — keep guest counts and reprice.
        if (hasGuestCounts) reprice()
      } else {
        // Duration changed — reset guest fields so the new duration's allowed types take effect.
        form.setFieldsValue({ main: { adults: 0, children: 0, infants: 0, passengers: 0 } })
        updatedSelections.counts = null
        updatedSelections.amount = 0
        updatedSelections.baseAmount = 0
        updatedSelections.breakdown = []
        updatedSelections.preCardTotal = 0
        updatedSelections.cardProcessingFee = 0
      }

      updateBookingSelections(updatedSelections)
      form.setFieldsValue({
        duration: enrichedTripLength?.value,
        time: isTripDurationUnchanged ? existingTimeSlotValue : null
      })
    },
    [
      updateBookingSelections,
      bookingSelections,
      existingTimeSlotValue,
      // experienceDetail is passed whole as `trip` (for progressive_pricing_*), which subsumes the
      // individual fields (guide_booking_fee_percentage, day_adjustments, etc.) also read off it below.
      experienceDetail,
      booking,
      effectivePaymentType,
      isEdit,
      isExperienceChanged,
      isGuestCountFixed,
      cardFeeInfo,
      appliedPromos,
      depositFraction,
      overrideGuestSubtotal,
      priceAdjustmentTotal,
      form
    ]
  )

  const handleTimeSlotChange = useCallback(
    (value) => {
      form.setFieldsValue({ time: value })
      updateBookingSelections({ timeSlot: value })
    },
    [updateBookingSelections, form]
  )

  const handleTimeSlotSelect = useCallback(
    (selected) => handleTimeSlotChange(selected.key?.slot || selected.key),
    [handleTimeSlotChange]
  )

  const handleGuestsChange = useCallback(
    (newCounts) => {
      // If the new counts return the booking to its saved state (e.g. the user changed a count and
      // then changed it back), restore the saved totals/breakdown verbatim instead of recomputing.
      // A fresh recalc of the card processing fee (gross-up rounding) can differ from the stored
      // value, making the fee appear to change even though nothing effectively did. Restoring keeps
      // the returned-to-original state identical to the initial edit view.
      if (isEdit && hasNoUserBookingChanges({ ...bookingSelections, counts: newCounts }, booking, isExperienceChanged, isPromoChanged)) {
        const savedTotal = getSavedBookingTotals(booking?.payment_summary).totalCharged
        const savedCardFee = Number(booking?.payment_summary?.card_processing_fee) || 0
        const computedBase = computeGuestSubtotalFromPaymentSummary(booking?.payment_summary)
        updateBookingSelections({
          counts: newCounts,
          amount: savedTotal,
          // Reverting to the original counts on an adjusted booking restores the negotiated base,
          // not the computed one, so the struck-through original stays alongside the adjusted price.
          baseAmount: overrideGuestSubtotal ?? computedBase,
          originalBaseAmount: computedBase,
          breakdown: buildBreakdownFromPaymentSummary(booking?.payment_summary),
          preCardTotal: savedTotal - savedCardFee,
          cardProcessingFee: savedCardFee
        })
        return
      }

      const guideFeePercent = experienceDetail?.guide_booking_fee_percentage || booking?.booking_fee_percentage
      const { total, guestSubtotal, calculatedGuestSubtotal, breakdown, preCardTotal, cardProcessingFee } = calculateBookingTotal(
        bookingSelections,
        newCounts,
        guideFeePercent,
        {
          paymentType: effectivePaymentType,
          isEdit,
          originalTaxAndFee: booking?.payment_summary?.tax_and_fee,
          customFees: experienceDetail?.assigned_custom_fees,
          platformFeeSetting: experienceDetail?.platform_fee_setting || booking?.platform_fee_setting,
          addOnsCatalog: experienceDetail?.add_ons,
          cardFeeInfo,
          originalCardProcessingFee: booking?.payment_summary?.card_processing_fee,
          priorCardCharge: getPriorCardCharge(booking),
          isCollectingNow,
          hasCollectedPayment: hasCollectedPayment(booking),
          discountPromos: resolveEffectiveDiscountPromos(appliedPromos, booking, isEdit && !isExperienceChanged && !isOriginalDiscountRemoved),
          dayAdjustments: experienceDetail?.day_adjustments,
          seasonalAdjustments: experienceDetail?.seasonal_adjustments,
          seasonRanges: experienceDetail?.available_date_ranges,
          trip: experienceDetail,
          depositFraction,
          overrideGuestSubtotal,
          priceAdjustmentTotal
        }
      ) || { total: 0, guestSubtotal: 0, calculatedGuestSubtotal: 0, breakdown: [], preCardTotal: 0, cardProcessingFee: 0 }

      updateBookingSelections({
        counts: newCounts,
        amount: total,
        baseAmount: guestSubtotal,
        originalBaseAmount: calculatedGuestSubtotal,
        preCardTotal,
        cardProcessingFee,
        breakdown
      })
    },
    [
      updateBookingSelections,
      bookingSelections,
      booking,
      effectivePaymentType,
      isEdit,
      // experienceDetail is passed whole as `trip` (for progressive_pricing_*), which subsumes the
      // individual fields (guide_booking_fee_percentage, day_adjustments, etc.) also read off it below.
      experienceDetail,
      cardFeeInfo,
      appliedPromos,
      isExperienceChanged,
      depositFraction,
      overrideGuestSubtotal,
      priceAdjustmentTotal
    ]
  )

  // For private experiences, GuestCountSection stores values in the form — sync them to bookingSelections
  const handlePrivateGuestChange = useCallback(
    (mainCounts) => {
      handleGuestsChange({ main: mainCounts, extra: bookingSelections?.counts?.extra || {} })
    },
    [handleGuestsChange, bookingSelections?.counts?.extra]
  )

  // ── Private experience asset allocation ────────────────────────────────────

  const debouncedFetchPrivateAssets = useMemo(
    () =>
      debounce((counts, tripId, date, timeSlot, isMultiDay) => {
        if (!tripId || !date || !counts) return
        if (!isMultiDay && !timeSlot) return

        const mainCounts = counts.main || {}
        const extraCounts = counts.extra || {}
        const adultCount = (mainCounts.adults || 0) + (extraCounts.adults || 0)
        const childCount = (mainCounts.children || 0) + (extraCounts.children || 0)
        const infantCount = (mainCounts.infants || 0) + (extraCounts.infants || 0)
        const passengerCount = (mainCounts.passengers || 0) + (extraCounts.passengers || 0)

        if (adultCount + childCount + infantCount + passengerCount === 0) return

        const params = {
          trip_id: tripId,
          date,
          adult_count: adultCount,
          child_count: childCount,
          infant_count: infantCount,
          passenger_count: passengerCount
        }
        if (timeSlot) params.time = timeSlot
        if (editingBookingUuid) params.b_uuid = editingBookingUuid
        if (assetClientParams) Object.assign(params, assetClientParams)

        dispatch(getAssetAllocation(params))
      }, 500),
    [dispatch, editingBookingUuid, assetClientParams]
  )

  // The slot a booking already holds needs no capacity check — it is allocated to this booking
  // already. Asking anyway greets the guide with "fully booked" the moment the modal opens, which is
  // how an expired request behaves: its slot has since been taken, and the form opens on it.
  // The check resumes as soon as a different date or time is picked.
  const isSavedSchedule =
    isEdit && bookingSelections?.date === booking?.booked_date && bookingSelections?.timeSlot === `${booking?.start_time} - ${booking?.end_time}`

  useEffect(() => {
    if (!isPrivateExperience || !isFeatureEnabled("NEW_RESOURCES") || !experienceDetail?.auto_assign_equipment) return
    if (isSavedSchedule) {
      // Going back to the saved slot after trying a full one must also clear that slot's error.
      dispatch(cleanAssetAllocation())
      return
    }
    const isMultiDay = privateDurationType === "multi_day"
    debouncedFetchPrivateAssets(
      bookingSelections?.counts,
      experienceDetail?.id,
      bookingSelections?.date,
      bookingSelections?.timeSlot,
      isMultiDay
    )
    return () => debouncedFetchPrivateAssets.cancel()
  }, [
    bookingSelections?.counts,
    bookingSelections?.date,
    bookingSelections?.timeSlot,
    experienceDetail?.id,
    experienceDetail?.auto_assign_equipment,
    isPrivateExperience,
    privateDurationType,
    isSavedSchedule,
    dispatch,
    debouncedFetchPrivateAssets
  ])

  // ── Effects ────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (isEdit && !experienceDetail?.id) return
    if (isEdit && hasNoUserBookingChanges(bookingSelections, booking, isExperienceChanged, isPromoChanged)) return
    const counts = bookingSelections?.counts
    const tripLength = bookingSelections?.tripLength
    const currentAmount = bookingSelections?.amount
    const hasGuestCounts =
      counts && (Object.values(counts.main || {}).some((v) => v > 0) || Object.values(counts.extra || {}).some((v) => v > 0))
    if (hasGuestCounts && tripLength) {
      const guideFeePercent = experienceDetail?.guide_booking_fee_percentage || booking?.booking_fee_percentage
      const { total, guestSubtotal, calculatedGuestSubtotal, breakdown, preCardTotal, cardProcessingFee } = calculateBookingTotal(
        bookingSelections,
        counts,
        guideFeePercent,
        {
          paymentType: effectivePaymentType,
          isEdit,
          originalTaxAndFee: booking?.payment_summary?.tax_and_fee,
          customFees: experienceDetail?.assigned_custom_fees,
          platformFeeSetting: experienceDetail?.platform_fee_setting || booking?.platform_fee_setting,
          addOnsCatalog: experienceDetail?.add_ons,
          cardFeeInfo,
          originalCardProcessingFee: booking?.payment_summary?.card_processing_fee,
          priorCardCharge: getPriorCardCharge(booking),
          isCollectingNow,
          hasCollectedPayment: hasCollectedPayment(booking),
          discountPromos: resolveEffectiveDiscountPromos(appliedPromos, booking, isEdit && !isExperienceChanged && !isOriginalDiscountRemoved),
          dayAdjustments: experienceDetail?.day_adjustments,
          seasonalAdjustments: experienceDetail?.seasonal_adjustments,
          seasonRanges: experienceDetail?.available_date_ranges,
          trip: experienceDetail,
          depositFraction,
          overrideGuestSubtotal,
          priceAdjustmentTotal
        }
      ) || { total: 0, guestSubtotal: 0, calculatedGuestSubtotal: 0, breakdown: [], preCardTotal: 0, cardProcessingFee: 0 }

      // Only update if amount is different (to avoid infinite loops)
      if (total !== currentAmount) {
        updateBookingSelections({
          amount: total,
          baseAmount: guestSubtotal,
          originalBaseAmount: calculatedGuestSubtotal,
          breakdown,
          preCardTotal,
          cardProcessingFee
        })
      }
    }
  }, [
    // experienceDetail is passed whole as `trip` (for progressive_pricing_*), which subsumes the
    // individual fields (id, guide_booking_fee_percentage, day_adjustments, etc.) read off it below.
    experienceDetail,
    booking,
    booking?.booking_fee_percentage,
    booking?.payment_summary?.tax_and_fee,
    booking?.platform_fee_setting,
    effectivePaymentType,
    bookingSelections?.counts,
    bookingSelections?.tripLength,
    bookingSelections?.amount,
    isEdit,
    isExperienceChanged,
    updateBookingSelections,
    bookingSelections,
    cardFeeInfo,
    appliedPromos,
    depositFraction,
    overrideGuestSubtotal,
    priceAdjustmentTotal
  ])

  // Load the detail for whichever experience is currently selected.
  //
  // The booking.trip.uuid fallback is ONLY for the very first load, before the watched form field has
  // settled AND before any detail has loaded. It must NOT fire on a step-2 → step-1 remount: there,
  // useWatch returns undefined on the first render while a (switched) detail is already loaded, and
  // falling back to the booking's ORIGINAL trip would refetch it over the switched one — which then
  // fires the asset-allocation/duration/slot effects with the old experience's id. Gating the
  // fallback on `!experienceDetail?.id` keeps it to the genuine initial load only.
  //
  // Guard against refetching an experience whose detail is already loaded (returning from step 2, or
  // the watched value settling) by comparing the loaded detail's id to the selection.
  //
  // The booking's own experience is fetched with the booking's uuid: for a past booking the backend
  // then answers with the day/seasonal adjustments it was SOLD under, which calculateBookingTotal
  // folds into the rates. Without it the guide's current rules come back, and adding an add-on to a
  // completed booking re-prices its guests. Any other experience is being priced now, so it is
  // fetched plain.
  const bookingTripUuid = booking?.trip?.uuid
  useEffect(() => {
    if (!selectedExperienceUuid) return
    if (isExperienceDetailCurrent) return
    const pricingBookingUuid = editingBookingUuid && selectedExperienceUuid === bookingTripUuid ? editingBookingUuid : ""
    dispatch(fetchExperienceById(selectedExperienceUuid, pricingBookingUuid, mode))
  }, [selectedExperienceUuid, isExperienceDetailCurrent, dispatch, mode, editingBookingUuid, bookingTripUuid])

  // For multi_day private experiences there is no duration-selection step, so tripLength is never
  // set by the user. Seed it from trip_prices[0] as soon as the experience detail loads so that
  // calculateBookingTotal has the pricing data (flat_rate, price_per_person, etc.) it needs.
  // In edit mode tripLength is already seeded from the saved booking (with its duration value, key
  // and price), so skip the overwrite: resetting value to "" makes change-detection treat the
  // unchanged booking as modified — triggering a spurious step-2 recalc that re-adds the card
  // processing fee — and swapping in trip_prices[0] can change the price the booking was made with.
  useEffect(() => {
    if (privateDurationType !== "multi_day") return
    // `experienceDetail`/`privateDurationType` can still describe the experience just switched AWAY
    // from (its own fetch may still be in flight) — see isExperienceDetailCurrent above. Seeding from
    // it here would be a one-way trip: the guard right below only fills in an EMPTY tripLength, so a
    // stale seed is never corrected once the real (new) experienceDetail arrives.
    if (!isExperienceDetailCurrent) return
    if (bookingSelections?.tripLength?.price) return
    // Fixed multi-day opens on its shortest duration so the calendar already knows how long a
    // block to paint; dynamic has a single daily-rate record and no duration step at all.
    const tripPrice = isFixedDays ? fixedDurationOptions[0]?.price : experienceDetail?.trip_prices?.[0]
    if (!tripPrice) return
    updateBookingSelections({ tripLength: { key: tripPrice.id, value: "", price: tripPrice } })
  }, [
    experienceDetail?.id,
    experienceDetail?.trip_prices,
    privateDurationType,
    isFixedDays,
    fixedDurationOptions,
    bookingSelections?.tripLength?.price,
    updateBookingSelections,
    isExperienceDetailCurrent
  ])

  // Set durationLoading as soon as there's an experience with durations to fetch
  useEffect(() => {
    if (experienceDetail?.id && selectedExperienceId && privateDurationType !== "multi_day") {
      setDurationLoading(true)
    }
  }, [selectedExperienceId, privateDurationType, experienceDetail?.id])

  // Fetch duration data as soon as the experience is selected (regular + single_day private) — a
  // date isn't required for this call, so durations (and the price they drive on the calendar) show
  // before the guide picks one. Durations are client-dependent too, so a client change re-runs this;
  // picking a duration from the refreshed list then drives the slots fetch below.
  useEffect(() => {
    if (!experienceDetail?.id || privateDurationType === "multi_day") return
    if (selectedExperienceId) {
      setDurationLoading(true)
      dispatch(fetchExperienceBookingDetails(selectedExperienceId, "trip_data", "", bookingSelections?.date, bookingId, mode, selectedClientId))
        .then(() => setDurationLoading(false))
        .catch(() => setDurationLoading(false))
    }
  }, [
    selectedExperienceId,
    dispatch,
    mode,
    bookingId,
    privateDurationType,
    bookingSelections?.date,
    experienceDetail?.id,
    selectedClientId
  ])

  // Multi-day has no time slots, but the same endpoint carries its availability: the bookable start
  // dates and, for dynamic, how long a stay can actually run from each one. Keyed on the duration
  // rather than on a date, because the calendar needs the map before a date is picked.
  useEffect(() => {
    if (!experienceDetail?.id || privateDurationType !== "multi_day") return
    const tripPriceId = bookingSelections?.tripLength?.price?.id
    if (!tripPriceId) return
    dispatch(
      fetchExperienceBookingDetails(selectedExperienceId, "available_slots", tripPriceId, "", bookingId, mode, selectedClientId)
    )
  }, [
    experienceDetail?.id,
    selectedExperienceId,
    privateDurationType,
    bookingSelections?.tripLength?.price?.id,
    bookingId,
    mode,
    selectedClientId,
    dispatch
  ])

  // Fetch available time slots when duration + date are set (regular + single_day private)
  useEffect(() => {
    if (!experienceDetail?.id || privateDurationType === "multi_day") return
    const tripLength = bookingSelections?.tripLength?.price?.id
    const tripDate = bookingSelections?.date
    // selectedClientId is part of the key: picking a different client after the date/duration are
    // already set must re-fetch, otherwise the slots stay tailored to the previous client.
    const callKey = `${selectedExperienceId}-${tripLength}-${tripDate}-${bookingId}-${selectedClientId}`
    if (tripLength && tripDate && selectedExperienceId) {
      if (lastSlotsFetchRef.current === callKey) return
      lastSlotsFetchRef.current = callKey
      setTimeLoading(true)
      dispatch(fetchExperienceBookingDetails(selectedExperienceId, "available_slots", tripLength, tripDate, bookingId, mode, selectedClientId))
        .then(() => setTimeLoading(false))
        .catch((error) => {
          console.error("Failed to fetch available slots:", error)
          setTimeLoading(false)
          updateBookingSelections({ timeSlot: null })
          form.setFieldsValue({ time: null })
        })
    } else {
      setTimeLoading(false)
      lastSlotsFetchRef.current = null
    }
  }, [
    bookingSelections?.tripLength?.price?.id,
    bookingSelections?.date,
    selectedExperienceId,
    dispatch,
    mode,
    bookingId,
    form,
    privateDurationType,
    updateBookingSelections,
    experienceDetail?.id,
    selectedClientId
  ])

  // The picker's disabledDate only tests the start date, so a multi-day trip booked at
  // the tail of a season can run past it. Advisory only — the booking is never blocked.
  const seasonalSpanWarning = useMemo(() => getSeasonalSpanWarning(experienceDetail, date), [experienceDetail, date])

  const showPrivateGuestSection =
    isPrivateExperience &&
    ((privateDurationType === "single_day" && !!bookingSelections?.timeSlot) ||
      (privateDurationType === "multi_day" && !!bookingSelections?.date))

  return (
    <Row gutter={16}>
      <Col xs={24} sm={24}>
        <Form.Item
          name="experience"
          label={t("booking.labels.experience")}
          rules={[{ required: true, message: t("booking.error_messages.experience_required") }]}
        >
          <Select
            labelInValue
            placeholder="Select Experience"
            showSearch
            disabled={isExperienceFixed}
            onChange={handleExperienceChange}
            filterOption={(input, option) => option?.label?.toLowerCase().includes(input.toLowerCase())}
            loading={experiencesListLoading}
          >
            {displayedExperiences?.map((trip) => (
              <Option key={trip.id} value={trip.uuid} label={trip.title}>
                {trip.title}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </Col>

      {/* ── Single date picker for all experience types ──────────────────── */}
      {(isEdit || selectedExperience?.value) && (
        <Col xs={24} sm={24}>
          {/* experienceLoading is checked BEFORE isPrivateExperience, not inside its branch: right
              after picking/switching an experience (or on first mount while editing), experienceDetail
              hasn't arrived yet, so isPrivateExperience itself is still the PREVIOUS answer (or
              false, on first load) — routing into the wrong branch below for that brief window
              and showing that branch's own stale/dimmed content. Checking loading first means
              neither branch below ever has to consider it, and always shows the same empty
              placeholder regardless of which type this turns out to be. */}
          {experienceLoading ? (
            <>
              <div className={`${bookingCalendarStyles.inlineCalendar} ${bookingCalendarStyles.calendarLoadingPlaceholder}`}>
                <Spin />
              </div>
              <Form.Item name="date" rules={[{ required: true, message: t("booking.error_messages.date_required") }]} hidden>
                <DatePicker />
              </Form.Item>
            </>
          ) : isPrivateExperience ? (
            // Private experiences (single_day and multi_day alike) all use the same calendar —
            // day/seasonal discount dots and per-day pricing included — so a guide sees the same
            // preview here as the guest-facing booking pickers.
            <>
              {isMultiDayAvailabilityPending ? (
                // The trip detail has loaded, but for multi-day the real per-day availability
                // (available_dates/booked_dates/max_span_by_date) is still a separate fetch —
                // same empty placeholder while that's in flight.
                <div className={`${bookingCalendarStyles.inlineCalendar} ${bookingCalendarStyles.calendarLoadingPlaceholder}`}>
                  <Spin />
                </div>
              ) : (
                <Space direction="vertical" size={12} className="w-100 mb-m">
                  {showDurationCards && (
                    <DurationSelector
                      options={fixedDurationOptions}
                      selectedKey={bookingSelections?.tripLength?.price?.id}
                      onSelect={handleDurationSelect}
                    />
                  )}
                  <DateSelector
                    trip={calendarTrip}
                    value={bookingSelections?.date ? dayjs(bookingSelections.date) : null}
                    onChange={isDynamicDays ? handleRangeChange : handleDateChange}
                    label={t(
                      !isMultiDayExperience
                        ? "booking.labels.booked_date"
                        : isDynamicDays
                          ? "booking.multi_day.select_start_end_date"
                          : "booking.multi_day.select_start_date"
                    )}
                    error={isDynamicDays && dateError}
                    errorMessage={t("booking.multi_day.end_date_required")}
                    isRange={isDynamicDays}
                    rangeValue={
                      isDynamicDays
                        ? {
                            from: bookingSelections?.date ? dayjs(bookingSelections.date).toDate() : undefined,
                            to: bookingSelections?.endDate ? dayjs(bookingSelections.endDate).toDate() : undefined
                          }
                        : undefined
                    }
                    minDays={minDurationDays}
                    maxDays={maxDurationDays}
                    maxSpanByDate={experienceBookingData?.bookingData?.data?.max_span_by_date}
                    availableDates={experienceBookingData?.bookingData?.data?.available_dates}
                    bookedDates={experienceBookingData?.bookingData?.data?.booked_dates}
                    spanDays={selectedTripDays}
                  />
                </Space>
              )}
              {/* Kept mounted regardless of the loading placeholder above, so the "date required"
                  rule stays registered with the form the whole time. */}
              <Form.Item name="date" rules={[{ required: true, message: t("booking.error_messages.date_required") }]} hidden>
                <DatePicker />
              </Form.Item>
            </>
          ) : (
            <Form.Item
              name="date"
              label={t("booking.labels.booked_date")}
              rules={[{ required: true, message: t("booking.error_messages.date_required") }]}
            >
              <DatePicker
                value={bookingSelections?.date ? dayjs(bookingSelections?.date) : null}
                style={{ width: "100%" }}
                disabledDate={disableDates}
                onChange={handleDateChange}
                format={BOOKING_DATE_FORMAT}
                open={datePickerOpen}
                onOpenChange={setDatePickerOpen}
              />
            </Form.Item>
          )}
          {seasonalSpanWarning && (
            <Alert
              type="warning"
              showIcon
              className="mb-m"
              message={t("booking.warnings.multi_day_out_of_season_title")}
              description={
                <Space direction="vertical" size={4}>
                  <Text>
                    {t(
                      seasonalSpanWarning.seasonEnd
                        ? "booking.warnings.multi_day_out_of_season"
                        : "booking.warnings.multi_day_out_of_season_no_end",
                      {
                        days: seasonalSpanWarning.days,
                        tripStart: seasonalSpanWarning.tripStartDate.format(BOOKING_DATE_FORMAT),
                        tripEnd: seasonalSpanWarning.tripEndDate.format(BOOKING_DATE_FORMAT),
                        seasonEnd: seasonalSpanWarning.seasonEnd?.format(BOOKING_DATE_FORMAT)
                      }
                    )}{" "}
                    {/* i18next 19 doesn't resolve _one/_other itself — the key is picked here,
                        matching the existing pattern in TipsDetails.jsx. */}
                    {t(
                      seasonalSpanWarning.outOfSeasonDates.length === 1
                        ? "booking.warnings.multi_day_out_of_season_days_one"
                        : "booking.warnings.multi_day_out_of_season_days_other",
                      { dates: formatDateSpan(seasonalSpanWarning.outOfSeasonDates) }
                    )}
                  </Text>
                  <Text type="secondary">{t("booking.warnings.multi_day_out_of_season_action")}</Text>
                </Space>
              }
            />
          )}
        </Col>
      )}

      {/* ── Duration — regular + single_day private ─────────────────────── */}
      {/* Shows as soon as the experience's durations have loaded, pre-selected — no need to wait on
          a date pick (the auto-select effect above seeds the first option, or the booking's own
          saved duration in edit mode). */}
      {durationTimeApplicable && (
        <DurationField
          t={t}
          durationLoading={durationLoading}
          updatedDurationSlots={updatedDurationSlots}
          selectedValue={bookingSelections?.tripLength?.key}
          onChange={handleTripDurationChange}
        />
      )}

      {/* ── Time slot — regular + single_day private ────────────────────── */}
      {durationTimeApplicable && bookingSelections?.tripLength && (
        <TimeSlotField
          t={t}
          timeLoading={timeLoading}
          updatedTimeSlots={updatedTimeSlots}
          selectedValue={bookingSelections?.timeSlot}
          onChange={handleTimeSlotSelect}
        />
      )}
      <GuestSection
        isBookingFixed={isGuestCountFixed}
        isPrivateExperience={isPrivateExperience}
        bookingSelections={bookingSelections}
        form={form}
        handleGuestsChange={handleGuestsChange}
        editingBookingUuid={editingBookingUuid}
        assetClientParams={assetClientParams}
        showPrivateGuestSection={showPrivateGuestSection}
        selectedTripPrice={selectedTripPrice}
        handlePrivateGuestChange={handlePrivateGuestChange}
        experienceDetail={experienceDetail}
        assetAllocation={assetAllocation}
      />

      {/* ── Common fields ────────────────────────────────────────────────── */}
      <Col xs={24} sm={12}>
        <Form.Item name="accessibility_required" valuePropName="checked">
          <Space align="center">
            <Switch value={accessibilityRequired} onChange={(val) => form.setFieldsValue({ accessibility_required: val })} />
            <span>{t("booking.labels.accessibility_service")}</span>
          </Space>
        </Form.Item>
      </Col>
      <Col xs={24} sm={12}>
        <NotifyClientField
          t={t}
          form={form}
          clientNotify={clientNotify}
          isNotifyLocked={isNotifyLocked}
          notifyError={notifyError}
          setNotifyError={setNotifyError}
        />
      </Col>
      <Col xs={24} sm={24}>
        <AccessibilityNotesField t={t} accessibilityRequired={accessibilityRequired} />
      </Col>
      <Col xs={24} sm={24}>
        <Form.Item label={t("booking.labels.notes")} name="notes">
          <TextArea rows={3} />
        </Form.Item>
      </Col>
    </Row>
  )
}

ExperienceDetail.propTypes = {
  form: PropTypes.object.isRequired,
  bookingId: PropTypes.string.isRequired,
  booking: PropTypes.object.isRequired,
  isEdit: PropTypes.bool,
  // Route match object from useMatch (or null), not a boolean — matches EditBookingForm's declaration
  bookAgain: PropTypes.object,
  mode: PropTypes.string,
  filterExperienceIds: PropTypes.arrayOf(PropTypes.number),
  cardFeeInfo: PropTypes.shape({ rate: PropTypes.number, fixed: PropTypes.number }),
  appliedPromos: PropTypes.array,
  isExperienceChanged: PropTypes.bool,
  isOriginalDiscountRemoved: PropTypes.bool,
  isPromoChanged: PropTypes.bool,
  isRecovery: PropTypes.bool,
  isPostTrip: PropTypes.bool,
  bookingSelections: PropTypes.object.isRequired,
  updateBookingSelections: PropTypes.func.isRequired,
  dateError: PropTypes.bool,
  setDateError: PropTypes.func,
  bookingSource: PropTypes.string
}

export default ExperienceDetail
